/**
 * Editorial cadence — canonical frontend mirror of the backend constants in
 * api_gateway/models/tendv2_settings.py. Per-channel publish pacing for the
 * queue: enabled (auto-publish arm), per_day (cadence), hours (weekday
 * windows, plain HH:MM strings displayed as browser-local time), strategy
 * (distribution strategy text). Resolution is per FIELD:
 * resolved[p] = {...default[p], ...universal[p], ...brand[p]};
 * order = brand array ?? universal array ?? CHANNEL_PLATFORMS.
 */

import { CHANNEL_PLATFORMS, ChannelPlatform } from './channelAspect';

export type CadenceWeekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export const CADENCE_WEEKDAYS: CadenceWeekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const WEEKDAY_LABELS: Record<CadenceWeekday, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

export interface CadenceHoursWindow {
  start: string; // 'HH:MM'
  end: string;   // 'HH:MM'
}

export type CadenceHours = Record<CadenceWeekday, CadenceHoursWindow | null>;

export interface ChannelCadence {
  enabled: boolean;
  per_day: number;
  hours: CadenceHours;
  strategy: string;
  /** Hours the channel's line pauses after the network flags a post as
      risky (TikTok spam_risk) — even if the account is re-linked sooner.
      0 = never pause. Rendered for TikTok; rides on every channel. */
  spam_cooldown_hours: number;
  /** Minutes the channel's line waits after the account is (re)linked
      before the next post, Publish now included — every spam flag on the
      test TikTok account followed a re-link and a post within minutes.
      0 = no hold. Rendered for TikTok; rides on every channel. */
  relink_hold_minutes: number;
}

export const SPAM_COOLDOWN_HOURS_OPTIONS = [0, 24, 48, 72, 96] as const;
export const RELINK_HOLD_MINUTES_OPTIONS = [0, 15, 30, 60, 120, 240] as const;

/** Sparse override shape (brand layer): any subset of fields per channel. */
export type ChannelCadenceOverride = Partial<ChannelCadence>;

export interface EditorialCadenceValue {
  channels: Record<string, ChannelCadence>;
  order: string[];
}

export interface EditorialCadenceOverride {
  channels: Record<string, ChannelCadenceOverride>;
  order: string[] | null;
}

const DEFAULT_HOURS: CadenceHours = {
  mon: { start: '09:00', end: '17:00' },
  tue: { start: '09:00', end: '17:00' },
  wed: { start: '09:00', end: '17:00' },
  thu: { start: '09:00', end: '17:00' },
  fri: { start: '09:00', end: '17:00' },
  sat: null,
  sun: null,
};

export const DEFAULT_CHANNEL_CADENCE: ChannelCadence = {
  enabled: false,
  per_day: 8,
  hours: DEFAULT_HOURS,
  strategy: '',
  spam_cooldown_hours: 48,
  relink_hold_minutes: 60,
};

/** Per-field resolution for one channel. */
export function resolveChannelCadence(
  brand: EditorialCadenceOverride | null | undefined,
  universal: EditorialCadenceValue | null | undefined,
  platform: string
): ChannelCadence {
  const u = universal?.channels?.[platform] ?? {};
  const b = brand?.channels?.[platform] ?? {};
  return {
    ...DEFAULT_CHANNEL_CADENCE,
    hours: { ...DEFAULT_HOURS },
    ...u,
    ...b,
  } as ChannelCadence;
}

/** Section/list order: brand override ?? universal ?? constant. */
export function resolveChannelOrder(
  brand: EditorialCadenceOverride | null | undefined,
  universal: EditorialCadenceValue | null | undefined
): ChannelPlatform[] {
  const candidate = brand?.order ?? universal?.order ?? [...CHANNEL_PLATFORMS];
  // Guard against stale/partial arrays: keep only known platforms, append
  // any missing ones in canonical order so all 13 always render.
  const known = candidate.filter((p): p is ChannelPlatform =>
    (CHANNEL_PLATFORMS as readonly string[]).includes(p));
  const missing = CHANNEL_PLATFORMS.filter((p) => !known.includes(p));
  return [...known, ...missing];
}

/** True if the brand layer overrides anything for this platform. */
export function hasChannelOverride(
  brand: EditorialCadenceOverride | null | undefined,
  platform: string
): boolean {
  return Object.keys(brand?.channels?.[platform] ?? {}).length > 0;
}

/** Backlog days for a channel: ceil(queued / per_day); null when cadence 0. */
export function backlogDays(queued: number, perDay: number): number | null {
  if (!perDay || perDay <= 0) return null;
  return Math.ceil(queued / perDay);
}

/** Channel display identity (name + brand color), shared by the Tended page
    and the Editorial Cadence settings. Rendering uses PlatformIcon. */
export const CHANNEL_INFO: Record<string, { name: string; color: string }> = {
  twitter: { name: 'Twitter', color: '#1DA1F2' },
  linkedin: { name: 'LinkedIn', color: '#0A66C2' },
  instagram: { name: 'Instagram', color: '#E4405F' },
  facebook: { name: 'Facebook', color: '#1877F2' },
  threads: { name: 'Threads', color: '#8a8f98' },
  tiktok: { name: 'TikTok', color: '#69C9D0' },
  youtube_shorts: { name: 'YouTube', color: '#FF0000' },
  bluesky: { name: 'Bluesky', color: '#0085FF' },
  reddit: { name: 'Reddit', color: '#FF4500' },
  pinterest: { name: 'Pinterest', color: '#E60023' },
  snapchat: { name: 'Snapchat', color: '#F7F400' },
  quora: { name: 'Quora', color: '#B92B27' },
  newsletter: { name: 'Newsletter', color: '#6FB6FF' },
};
